import React, { useState, useEffect } from 'react';
import ReactDom from 'react-dom';
import { Button } from 'lib/components';
import InventorySelect from './InventorySelect';
import { Inventory, JobInventory } from 'types';
import classes from '../index.module.scss';

/**
 * Props interface for the InventoryInputModal component
 */
interface Props {
	/** Array of available inventory items to select from */
	items: Array<Inventory>;
	/** Whether the modal is currently open */
	isOpen: boolean;
	/** Function to set the modal's open state */
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	/** Callback function when the modal is closed */
	onClose: () => any;
	/** Callback function to add a new inventory item */
	addInventory: (inventory: JobInventory) => any;
	/** Callback function to edit an existing inventory item */
	editInventory: (inventory: JobInventory) => any;
	/** Optional existing item to edit */
	item?: JobInventory;
	/** Array of inventory IDs that are already selected */
	selected: Array<number>;
}

/**
 * InventoryInputModal component
 * Provides a modal dialog for adding or editing inventory items
 * Allows selecting an inventory item and specifying the amount
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element | null} - Rendered component or null if not open
 */
const InventoryInputModal = ({
	items,
	item,
	selected,
	isOpen,
	setIsOpen,
	addInventory,
	editInventory,
	onClose,
}: Props) => {
	// State for the selected inventory item ID
	const [inventoryId, setInventoryId] = useState<number | undefined>();
	// State for the inventory item name
	const [name, setName] = useState<string | undefined>();
	// State for the inventory amount
	const [amount, setAmount] = useState(0);

	/**
	 * Initialize form values when editing an existing item
	 * Clears form values when adding a new item
	 */
	useEffect(() => {
		if (item) {
			// Editing mode - populate form with existing values
			setInventoryId(item.inventoryId);
			setName(item.name);
			setAmount(item.amount);
		} else {
			// Adding mode - reset form values
			setInventoryId(undefined);
			setName('');
			setAmount(0);
			setIsOpen(false);
		}
	}, [item]);

	/**
	 * Handle keyboard events in the amount input field
	 * Allows submitting the form with Enter or closing with Escape
	 *
	 * @param {React.KeyboardEvent<HTMLInputElement>} e - Keyboard event
	 */
	const addItem = (e: React.KeyboardEvent<HTMLInputElement>) => {
		// Close modal on Escape key
		if (e.key === 'Escape') {
			setIsOpen(false);
		}

		// Submit form on Enter key
		if (e.key === 'Enter') {
			if (item) {
				// Update existing inventory item
				editInventory({ amount, inventoryId: inventoryId as number, name: name as string });
			} else {
				// Add new inventory item
				addInventory({ amount, inventoryId: inventoryId as number, name: name as string });
			}
			// Reset form values
			setInventoryId(undefined);
			setName('');
			setAmount(0);
			setIsOpen(false);
		}
	};

	// Don't render anything if the modal is closed
	if (!isOpen) {
		return null;
	}

	// Render the modal using a portal to ensure it appears above other content
	return ReactDom.createPortal(
		<div className={classes['modal']}>
			<div className={classes['modal-content']}>
				{/* Form content area */}
				<div style={{ padding: '4rem 2rem 0rem' }}>
					<div className={classes['inventory-input-container']}>
						{/* Inventory item selector dropdown */}
						<InventorySelect
							items={items}
							// Disable selection when editing an existing item
							disabled={item ? true : false}
							selected={selected}
							value={inventoryId}
							setValue={setInventoryId}
							setLabel={setName}
						/>
						{/* Amount input field */}
						<div className={classes['amount-input-container']}>
							<input
								type="number"
								className={classes['amount-input']}
								value={amount}
								onChange={(e) => setAmount(e.target.valueAsNumber)}
								onKeyDown={addItem}
							/>
						</div>
					</div>
				</div>
				{/* Action buttons */}
				<div className={classes['modal-buttons']}>
					{/* Add/Update button */}
					<Button
						variant="standard"
						title={item ? 'Update' : 'Add'}
						onClick={() => {
							if (name && amount && inventoryId) {
								if (item) {
									// Update existing inventory item
									editInventory({ amount, inventoryId, name });
								} else {
									// Add new inventory item
									addInventory({ amount, inventoryId, name });
								}
								// Reset form values and close modal
								setInventoryId(undefined);
								setName('');
								setAmount(0);
								setIsOpen(false);
							}
						}}
						// Disable button if any required field is empty
						disabled={!(name && amount && inventoryId)}
					/>
					{/* Cancel button */}
					<Button color="gray" variant="standard" title="Cancel" onClick={onClose} />
				</div>
			</div>
		</div>,
		// Mount the portal in the designated element in the DOM
		document.getElementById('portal') as HTMLElement
	);
};

export default InventoryInputModal;
